import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import type { User } from "firebase/auth";
import type { OrganizationRecord } from "../../../api/organizations";
import {
  deleteOrganization,
  fetchOrganizationDeletionPreview,
  type OrganizationDeletionPreview,
} from "../../../api/organizations";

interface DeleteOrganizationModalProps {
  show: boolean;
  onHide: () => void;
  org: OrganizationRecord | null;
  user: User;
  onDeleted: (
    organizationId: string,
    deletedLinkedSandboxId?: string | null,
  ) => void;
}

export const DeleteOrganizationModal: React.FC<DeleteOrganizationModalProps> = ({
  show,
  onHide,
  org,
  user,
  onDeleted,
}) => {
  const [preview, setPreview] = useState<OrganizationDeletionPreview | null>(
    null,
  );
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [confirmName, setConfirmName] = useState("");
  const [deleteLinkedSandbox, setDeleteLinkedSandbox] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (!show || !org) {
      setPreview(null);
      setPreviewError(null);
      setConfirmName("");
      setDeleteLinkedSandbox(false);
      setAcknowledged(false);
      setDeleteError(null);
      return;
    }

    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    fetchOrganizationDeletionPreview(user, org.id)
      .then((data) => {
        if (!cancelled) {
          setPreview(data);
          setDeleteLinkedSandbox(Boolean(data.linkedSandboxOrganizationId));
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setPreviewError(
            e instanceof Error ? e.message : "Failed to load deletion preview.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show, org, user]);

  const nameMatches =
    preview != null && confirmName.trim() === preview.name.trim();
  const hasLinkedSandbox = Boolean(preview?.linkedSandboxOrganizationId);
  const blockedBySandbox = hasLinkedSandbox && !deleteLinkedSandbox;
  const canDelete =
    preview != null &&
    nameMatches &&
    acknowledged &&
    !blockedBySandbox &&
    !deleting &&
    !previewLoading;

  const handleDelete = async () => {
    if (!org || !preview || !canDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const result = await deleteOrganization(user, org.id, {
        confirmName: confirmName.trim(),
        deleteLinkedSandbox,
      });
      onDeleted(org.id, result.deletedLinkedSandboxId);
      onHide();
    } catch (e) {
      setDeleteError(
        e instanceof Error ? e.message : "Failed to delete organization.",
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => !deleting && onHide()}
      centered
      fullscreen="sm-down"
    >
      <Modal.Header closeButton={!deleting}>
        <Modal.Title>Delete organization</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {org && (
          <p className="text-muted small mb-3">
            {org.name} <span className="text-secondary">({org.id})</span>
          </p>
        )}

        {previewLoading ? (
          <div className="d-flex align-items-center gap-2 text-muted">
            <Spinner animation="border" size="sm" />
            <span>Loading deletion impact…</span>
          </div>
        ) : previewError ? (
          <Alert variant="danger" className="mb-0">
            {previewError}
          </Alert>
        ) : preview ? (
          <>
            <Alert variant="danger">
              <strong>Permanent deletion.</strong> This removes all business data
              for this organization from Firestore and cannot be undone.
            </Alert>

            {preview.warnings.length > 0 && (
              <ul className="small text-muted mb-3">
                {preview.warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            )}

            {hasLinkedSandbox && (
              <Form.Check
                type="checkbox"
                id="delete-linked-sandbox"
                className="mb-3"
                label={`Also delete linked sandbox (${preview.linkedSandboxOrganizationId})`}
                checked={deleteLinkedSandbox}
                onChange={(e) => setDeleteLinkedSandbox(e.target.checked)}
                disabled={deleting}
              />
            )}

            {blockedBySandbox && (
              <Alert variant="warning" className="small">
                Confirm sandbox deletion above to proceed with this production
                organization.
              </Alert>
            )}

            <Form.Check
              type="checkbox"
              id="delete-org-ack"
              className="mb-3"
              label="I understand this action is irreversible."
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              disabled={deleting}
            />

            <Form.Group controlId="delete-org-confirm-name">
              <Form.Label>
                Type <strong>{preview.name}</strong> to confirm
              </Form.Label>
              <Form.Control
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                placeholder={preview.name}
                autoComplete="off"
                disabled={deleting}
              />
            </Form.Group>

            {deleteError && (
              <Alert variant="danger" className="mt-3 mb-0">
                {deleteError}
              </Alert>
            )}
          </>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={deleting}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={() => void handleDelete()}
          disabled={!canDelete}
        >
          {deleting ? "Deleting…" : "Delete permanently"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
